import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Mail, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Users,
  Calendar,
  XCircle,
  Loader2,
  Eye
} from 'lucide-react';
import Button from '../components/Button';
import Chip from '../components/Chip';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import api from '../lib/api';
import toast from 'react-hot-toast';
import Modal from '@shared/ui/Modal';
import RefreshButton from '../components/RefreshButton';
import { StandardTableHeader } from '../components/StandardTable';

interface ChamberStatus {
  scraped: boolean;
  eventCount: number;
  billCount: number;
  lastScrapedAt: string | null;
}

interface EmailStatus {
  targetDate: string;
  targetDateFormatted: string;
  chambers: {
    House: ChamberStatus;
    Senate: ChamberStatus;
    Joint: ChamberStatus;
  };
  totalEvents: number;
  allChambersReady: boolean;
  calendarReadyByScrapeDate: boolean;
  lastScrapeTargetDate: string | null;
  subscriberCount: number;
  sendEligibleCount: number;
  latestJob: {
    id: string;
    status: string;
    started_at: string;
    completed_at: string;
    emails_sent: number;
    emails_failed: number;
    total_recipients: number;
  } | null;
  warnings: string[];
  canSendEmails: boolean;
}

interface EmailJob {
  id: string;
  target_date: string;
  status: string;
  triggered_by: string;
  triggered_by_email: string;
  triggered_by_name: string;
  allow_partial_chambers: boolean;
  total_recipients: number;
  emails_sent: number;
  emails_failed: number;
  started_at: string;
  completed_at: string;
  error_message: string;
  created_at: string;
}

interface SendLogEntry {
  id: string;
  email_address: string;
  status: string;
  error_message: string | null;
  workspace_count: number;
  bill_count: number;
  event_count: number;
  sent_at: string | null;
  created_at: string;
}

interface JobDetails extends EmailJob {
  sendLog: SendLogEntry[];
}

const AdminCalendarEmailsPage: React.FC = () => {
  const { setPageMetadata } = usePageMetadata();
  const [status, setStatus] = useState<EmailStatus | null>(null);
  const [jobs, setJobs] = useState<EmailJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [targetDate, setTargetDate] = useState<'tomorrow' | 'today'>('tomorrow');
  const [allowPartialChambers, setAllowPartialChambers] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Set page metadata for Layout back button
  useEffect(() => {
    setPageMetadata({
      title: 'Calendar Emails',
      description: 'Send nightly calendar digest emails',
      backUrl: '/admin'
    });
    return () => setPageMetadata(null);
  }, [setPageMetadata]);

  // Calculate dates for display - memoized to prevent infinite re-renders
  const { today, tomorrow, todayStr, tomorrowStr } = useMemo(() => {
    const getMtParts = (date: Date) => {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Denver',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const parts = formatter.formatToParts(date);
      const partMap = Object.fromEntries(parts.map(part => [part.type, part.value]));
      return {
        year: partMap.year,
        month: partMap.month,
        day: partMap.day
      };
    };
    
    const toMtDate = (date: Date) => {
      const { year, month, day } = getMtParts(date);
      return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0));
    };
    
    const formatForApi = (date: Date) => {
      const { year, month, day } = getMtParts(date);
      return `${year}-${month}-${day}`;
    };
    
    const todayMt = toMtDate(new Date());
    const tomorrowMt = new Date(todayMt);
    tomorrowMt.setUTCDate(tomorrowMt.getUTCDate() + 1);
    
    return {
      today: todayMt,
      tomorrow: tomorrowMt,
      todayStr: formatForApi(todayMt),
      tomorrowStr: formatForApi(tomorrowMt)
    };
  }, []);

  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Denver',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if a timestamp is "today" in the user's local timezone
  const getMtDateString = (date: Date): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Denver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(date);
    const partMap = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${partMap.year}-${partMap.month}-${partMap.day}`;
  };
  
  const isScrapedToday = (lastScrapedAt: string | null): boolean => {
    if (!lastScrapedAt) return false;
    const scrapedDate = new Date(lastScrapedAt);
    return getMtDateString(scrapedDate) === getMtDateString(new Date());
  };

  // Format a timestamp for display in user's local timezone
  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const fetchStatus = useCallback(async () => {
    try {
      const dateParam = targetDate === 'today' ? todayStr : tomorrowStr;
      const response = await api.get(`/calendar-emails/status?date=${dateParam}`);
      setStatus(response.data.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      toast.error('Failed to fetch email status');
    }
  }, [targetDate, todayStr, tomorrowStr]);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await api.get('/calendar-emails/jobs?limit=10');
      setJobs(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, []);

  const fetchJobDetails = async (jobId: string) => {
    setDetailsLoading(true);
    try {
      const response = await api.get(`/calendar-emails/jobs/${jobId}`);
      setSelectedJob(response.data.data);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchJobs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchJobs]);

  // Auto-refresh status every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStatus();
      fetchJobs();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchJobs]);

  const handleSendEmails = async () => {
    if (!status?.canSendEmails) {
      toast.error('No subscribers to send emails to');
      return;
    }

    const readyByScrapeDate = status?.calendarReadyByScrapeDate ?? false;

    if (!readyByScrapeDate && !allowPartialChambers) {
      toast.error('Calendar scraper has not completed for the target date. Enable "Allow Partial Data" to send anyway.');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/calendar-emails/jobs', {
        targetDate,
        allowPartialChambers
      });
      
      toast.success(`Email job started! Job ID: ${response.data.data.jobId}`);
      
      // Refresh data
      await Promise.all([fetchStatus(), fetchJobs()]);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to start email job');
    } finally {
      setSending(false);
    }
  };

  const getJobStatusChip = (jobStatus: string) => {
    switch (jobStatus) {
      case 'completed':
        return <Chip label="Completed" variant="success" />;
      case 'running':
        return <Chip label="Running" variant="info" />;
      case 'failed':
        return <Chip label="Failed" variant="danger" />;
      case 'cancelled':
        return <Chip label="Cancelled" variant="warning" />;
      default:
        return <Chip label={jobStatus} variant="neutral" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-text-primary dark:text-text-dark-primary">
            Calendar Email Digest
          </h1>
          <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
            Send nightly calendar digest emails to subscribed users
          </p>
        </div>
        <RefreshButton
          onRefresh={() => {
            fetchStatus();
            fetchJobs();
          }}
        />
      </div>

      {/* Status Card */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Scraper Status
        </h2>

        {/* Target Date Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
            Target Date
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={targetDate === 'tomorrow'}
                onChange={() => setTargetDate('tomorrow')}
                className="w-4 h-4 text-primary-500"
              />
              <span className="text-text-primary dark:text-text-dark-primary">
                Tomorrow ({formatDateDisplay(tomorrow)})
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={targetDate === 'today'}
                onChange={() => setTargetDate('today')}
                className="w-4 h-4 text-primary-500"
              />
              <span className="text-text-primary dark:text-text-dark-primary">
                Today ({formatDateDisplay(today)})
              </span>
            </label>
          </div>
        </div>

        {/* Chamber Status Grid */}
        {status && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            {(['House', 'Senate', 'Joint'] as const).map((chamber) => {
              const chamberStatus = status.chambers[chamber];
              const scrapedToday = isScrapedToday(chamberStatus.lastScrapedAt);
              return (
                <div
                  key={chamber}
                  className="p-4 surface-inset text-center"
                >
                  <div className="font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                    {chamber}
                  </div>
                  {chamberStatus.eventCount > 0 ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span>{chamberStatus.eventCount} events</span>
                    </div>
                  ) : scrapedToday ? (
                    <div className="text-text-secondary dark:text-text-dark-secondary text-sm">
                      No events
                    </div>
                  ) : chamberStatus.scraped ? (
                    <div className="text-yellow-600 text-sm">
                      No recent scrape
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-text-tertiary dark:text-text-dark-tertiary">
                      <XCircle className="w-4 h-4" />
                      <span>Not scraped</span>
                    </div>
                  )}
                  {chamberStatus.lastScrapedAt && (
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                      Last: {formatTimestamp(chamberStatus.lastScrapedAt)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Warnings - show if House or Senate not scraped today */}
        {status && (!isScrapedToday(status.chambers.House?.lastScrapedAt) || 
                    !isScrapedToday(status.chambers.Senate?.lastScrapedAt)) && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            {!isScrapedToday(status.chambers.House?.lastScrapedAt) && (
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  House calendar has not been scraped today
                  {status.chambers.House?.lastScrapedAt && 
                    ` (last: ${formatTimestamp(status.chambers.House.lastScrapedAt)})`}
                </span>
              </div>
            )}
            {!isScrapedToday(status.chambers.Senate?.lastScrapedAt) && (
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Senate calendar has not been scraped today
                  {status.chambers.Senate?.lastScrapedAt && 
                    ` (last: ${formatTimestamp(status.chambers.Senate.lastScrapedAt)})`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* All Ready Indicator */}
        {status && isScrapedToday(status.chambers.House?.lastScrapedAt) && 
         isScrapedToday(status.chambers.Senate?.lastScrapedAt) && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle className="w-4 h-4" />
              <span>Both House and Senate have been scraped today - ready to send emails!</span>
            </div>
          </div>
        )}

        {/* Recipient counts */}
        <div className="space-y-1 mb-4">
          <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
            <Users className="w-4 h-4" />
            <span>
              {status?.sendEligibleCount ?? status?.subscriberCount ?? 0} recipients will receive emails
            </span>
          </div>
          {status && status.subscriberCount > status.sendEligibleCount && (
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-6">
              {status.subscriberCount} subscribed total — {status.subscriberCount - status.sendEligibleCount} lack
              an email or eligible workspace and are skipped
            </p>
          )}
        </div>

        {/* Allow Partial Option */}
        {status && !(isScrapedToday(status.chambers.House?.lastScrapedAt) && 
                     isScrapedToday(status.chambers.Senate?.lastScrapedAt)) && (
          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={allowPartialChambers}
              onChange={(e) => setAllowPartialChambers(e.target.checked)}
              className="w-4 h-4 text-primary-500"
            />
            <span className="text-text-primary dark:text-text-dark-primary">
              Allow sending with partial data (some chambers not scraped today)
            </span>
          </label>
        )}

        {/* Send Button */}
        <Button
          variant="primary"
          icon={sending ? Loader2 : Mail}
          onClick={handleSendEmails}
          disabled={sending || !status?.canSendEmails}
          className={sending ? 'animate-pulse' : ''}
        >
          {sending ? 'Sending...' : 'Send Calendar Emails'}
        </Button>
      </div>

      {/* Job History */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Recent Jobs
        </h2>

        {jobs.length === 0 ? (
          <p className="text-text-secondary dark:text-text-dark-secondary">
            No email jobs have been run yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <StandardTableHeader>
                <tr>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Date
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Status
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Sent
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Failed
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Triggered By
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Started
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Actions
                  </th>
                </tr>
              </StandardTableHeader>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className="border-b border-border-light dark:border-border-dark hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    <td className="py-3 px-3 text-text-primary dark:text-text-dark-primary">
                      {new Date(job.target_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="py-3 px-3">
                      {getJobStatusChip(job.status)}
                    </td>
                    <td className="py-3 px-3 text-text-primary dark:text-text-dark-primary">
                      {job.emails_sent || 0}
                    </td>
                    <td className="py-3 px-3">
                      {job.emails_failed > 0 ? (
                        <span className="text-red-600">{job.emails_failed}</span>
                      ) : (
                        <span className="text-text-tertiary dark:text-text-dark-tertiary">0</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-text-secondary dark:text-text-dark-secondary">
                      {job.triggered_by_name || job.triggered_by_email || 'Unknown'}
                    </td>
                    <td className="py-3 px-3 text-text-secondary dark:text-text-dark-secondary">
                      {job.started_at
                        ? new Date(job.started_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="py-3 px-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Eye}
                        onClick={() => fetchJobDetails(job.id)}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Job Details Modal */}
      {(selectedJob || detailsLoading) && (
        <Modal
          isOpen={true}
          title="Job details"
          onClose={() => setSelectedJob(null)}
          size="xl"
          footer={
            <Button variant="secondary" onClick={() => setSelectedJob(null)}>
              Close
            </Button>
          }
        >
              {detailsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : selectedJob && (
                <div className="space-y-4">
                  {/* Job Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 surface-inset">
                    <div>
                      <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Status</div>
                      <div className="mt-1">{getJobStatusChip(selectedJob.status)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Target Date</div>
                      <div className="text-text-primary dark:text-text-dark-primary">
                        {new Date(selectedJob.target_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Emails Sent</div>
                      <div className="text-text-primary dark:text-text-dark-primary">{selectedJob.emails_sent || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Emails Failed</div>
                      <div className={selectedJob.emails_failed > 0 ? 'text-red-600' : 'text-text-primary dark:text-text-dark-primary'}>
                        {selectedJob.emails_failed || 0}
                      </div>
                    </div>
                  </div>

                  {/* Job Error */}
                  {selectedJob.error_message && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <div className="font-medium text-red-700 dark:text-red-400">Job Error</div>
                          <div className="text-sm text-red-600 dark:text-red-300 mt-1">{selectedJob.error_message}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Send Log */}
                  <div>
                    <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-2">
                      Send Log ({selectedJob.sendLog?.length || 0} recipients)
                    </h4>
                    {selectedJob.sendLog && selectedJob.sendLog.length > 0 ? (
                      <div className="overflow-x-auto border border-border-light dark:border-border-dark rounded-lg">
                        <table className="w-full text-sm">
                          <StandardTableHeader>
                            <tr>
                              <th className="text-left py-2 px-3 font-medium text-text-secondary dark:text-text-dark-secondary">
                                Email
                              </th>
                              <th className="text-left py-2 px-3 font-medium text-text-secondary dark:text-text-dark-secondary">
                                Status
                              </th>
                              <th className="text-left py-2 px-3 font-medium text-text-secondary dark:text-text-dark-secondary">
                                Workspaces
                              </th>
                              <th className="text-left py-2 px-3 font-medium text-text-secondary dark:text-text-dark-secondary">
                                Bills
                              </th>
                              <th className="text-left py-2 px-3 font-medium text-text-secondary dark:text-text-dark-secondary">
                                Error
                              </th>
                            </tr>
                          </StandardTableHeader>
                          <tbody>
                            {selectedJob.sendLog.map((entry) => (
                              <tr key={entry.id} className="border-b border-border-light dark:border-border-dark last:border-b-0">
                                <td className="py-2 px-3 text-text-primary dark:text-text-dark-primary">
                                  {entry.email_address}
                                </td>
                                <td className="py-2 px-3">
                                  {entry.status === 'sent' ? (
                                    <span className="flex items-center gap-1 text-green-600">
                                      <CheckCircle className="w-3 h-3" /> Sent
                                    </span>
                                  ) : entry.status === 'failed' ? (
                                    <span className="flex items-center gap-1 text-red-600">
                                      <XCircle className="w-3 h-3" /> Failed
                                    </span>
                                  ) : (
                                    <span className="text-text-tertiary dark:text-text-dark-tertiary">{entry.status}</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-text-secondary dark:text-text-dark-secondary">
                                  {entry.workspace_count}
                                </td>
                                <td className="py-2 px-3 text-text-secondary dark:text-text-dark-secondary">
                                  {entry.bill_count}
                                </td>
                                <td className="py-2 px-3 text-red-600 text-xs max-w-xs truncate" title={entry.error_message || ''}>
                                  {entry.error_message || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-text-tertiary dark:text-text-dark-tertiary">
                        No send log entries found.
                      </p>
                    )}
                  </div>
                </div>
              )}
        </Modal>
      )}
    </div>
  );
};

export default AdminCalendarEmailsPage;
